import { useState, type ReactNode } from "react";
import { api, type DemandReport, type ProductRow, type SocialReport } from "../api";
import { useAsync } from "../useAsync";
import { count, delta, DASH } from "../format";
import { go } from "../router";

const RANGES = [7, 30, 90];
const TABS = [
  { id: "search" as const, label: "Search" },
  { id: "social" as const, label: "Social" },
];

const FLAG_LABEL: Record<DemandReport["queries"][number]["flag"], string> = {
  striking_distance: "striking distance",
  low_ctr: "low CTR",
  declining: "declining",
  none: "",
};

/**
 * Demand: search impressions → clicks → visits → leads, plus queries, ranks,
 * AI visibility and authority. Reads only GET /demand.
 */
export function DemandGen({ product }: { product: ProductRow }) {
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("search");
  const report = useAsync(() => api.demand(product.slug, days), [product.slug, days]);
  const social = useAsync(() => api.social(product.slug, days), [product.slug, days]);

  if (tab === "search" && report.error) return <PageProblem message={report.error} />;
  if (tab === "search" && (report.loading || !report.data)) return <PageLoading />;
  if (tab === "social" && social.error) return <PageProblem message={social.error} />;
  if (tab === "social" && (social.loading || !social.data)) return <PageLoading />;

  return (
    <div className="page" style={{ paddingTop: 26 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h1>Demand</h1>
          <span className="sub">
            {tab === "social"
              ? `Organic social for ${product.name} — audience, reach, and the posts that earned it.`
              : `Search and AI-search demand for ${product.name} — queries, ranks, visibility, and what to connect next.`}
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6 }}>
          {RANGES.map((d) => (
            <button key={d} type="button" className="seg" aria-pressed={days === d} onClick={() => setDays(d)}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, padding: "18px 0 0" }}>
        {TABS.map((t) => (
          <button key={t.id} type="button" className="seg" aria-pressed={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "social" && social.data && <SocialBody r={social.data} />}
      {tab === "search" && report.data && <SearchBody r={report.data} />}
    </div>
  );
}

function SearchBody({ r }: { r: DemandReport }) {
  const missing = r.sources.filter((s) => !s.connected);

  return (
    <>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: 14, padding: "28px 0 24px", borderBottom: "1px solid var(--border)" }}>
        <FunnelCell label="Impressions" now={r.funnel.impressions} prev={r.funnel.prev.impressions} />
        <FunnelCell label="Clicks" now={r.funnel.clicks} prev={r.funnel.prev.clicks} />
        <FunnelCell label="Visits" now={r.funnel.visits} prev={r.funnel.prev.visits} />
        <FunnelCell label="Leads" now={r.funnel.leads} prev={r.funnel.prev.leads} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    gap: 14, padding: "24px 0" }}>
        <div className="panel" style={{ padding: "16px 18px" }}>
          <div className="eyebrow">AI visibility</div>
          <div className="num" style={{ fontSize: 24, marginTop: 7 }}>
            {r.ai_visibility.score != null ? r.ai_visibility.score.toFixed(1) : DASH}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 6 }}>
            {r.ai_visibility.mention_rate != null
              ? `Mentioned in ${Math.round(r.ai_visibility.mention_rate * 100)}% of tracked answers`
              : "Connect Opinly to measure mention rate"}
          </div>
        </div>
        <div className="panel" style={{ padding: "16px 18px" }}>
          <div className="eyebrow">Authority</div>
          <div className="num" style={{ fontSize: 24, marginTop: 7 }}>
            {r.authority.domain_rating != null ? r.authority.domain_rating : DASH}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 6 }}>
            {r.authority.source
              ? `${r.authority.source}${r.authority.referring_domains != null ? ` · ${r.authority.referring_domains.toLocaleString()} referring domains` : ""}`
              : "Connect Ahrefs or SEMrush for domain rating"}
          </div>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="panel" style={{ padding: "14px 18px", marginBottom: 22,
                                        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--text-2)" }}>
            Not connected: {missing.map((s) => s.label).join(", ")}.
          </span>
          <button type="button" className="btn-ghost" onClick={() => go({ name: "integrations" })}>
            Connect sources
          </button>
        </div>
      )}

      <Section title="Queries" note={`${r.queries.length} in this window`}>
        {r.queries.length === 0 ? (
          <Empty hint="Connect Search Console or Bing Webmaster to see queries." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Query</th>
                <th className="num">Clicks</th>
                <th className="num">Impressions</th>
                <th className="num">CTR</th>
                <th className="num">Pos</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {r.queries.map((q) => (
                <tr key={q.query}>
                  <td>{q.query}</td>
                  <td className="num">{count(q.clicks)}</td>
                  <td className="num">{count(q.impressions)}</td>
                  <td className="num">{`${Math.round(q.ctr * 1000) / 10}%`}</td>
                  <td className="num">{q.position != null ? q.position.toFixed(1) : DASH}</td>
                  <td style={{ color: "var(--warn)", fontSize: 11.5 }}>{FLAG_LABEL[q.flag]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Keyword ranks" note="Latest rank per keyword across SEMrush, Ahrefs and Opinly">
        {r.ranks.length === 0 ? (
          <Empty hint="Connect SEMrush, Ahrefs or Opinly to see ranked keywords." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Keyword</th>
                <th className="num">Position</th>
                <th className="num">Volume</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {r.ranks.map((k) => (
                <tr key={`${k.source}:${k.keyword}`}>
                  <td>{k.keyword}</td>
                  <td className="num">{k.position != null ? k.position.toFixed(1) : DASH}</td>
                  <td className="num">{k.volume != null ? count(k.volume) : DASH}</td>
                  <td style={{ fontSize: 12, color: "var(--text-3)" }}>{k.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </>
  );
}

function SocialBody({ r }: { r: SocialReport }) {
  const missing = r.networks.filter((n) => !n.connected);
  const growth = r.audience.followers - r.audience.followers_prev;
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: 14, padding: "28px 0 24px", borderBottom: "1px solid var(--border)" }}>
        <FunnelCell label="Followers" now={r.audience.followers} prev={r.audience.followers_prev} />
        <div>
          <div className="eyebrow">Net growth</div>
          <div className="num" style={{ fontSize: 24, letterSpacing: "-0.03em", marginTop: 6 }}>
            {growth > 0 ? `+${count(growth)}` : count(growth)}
          </div>
        </div>
        <div>
          <div className="eyebrow">Reach / impressions</div>
          <div className="num" style={{ fontSize: 24, letterSpacing: "-0.03em", marginTop: 6 }}>{count(r.audience.impressions)}</div>
          <div className="num" style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 4 }}>
            {r.audience.reach > 0 ? `${count(r.audience.reach)} reach` : "impressions in window"}
          </div>
        </div>
        <div>
          <div className="eyebrow">Engagement rate</div>
          <div className="num" style={{ fontSize: 24, letterSpacing: "-0.03em", marginTop: 6 }}>
            {r.audience.engagement_rate != null ? `${Math.round(r.audience.engagement_rate * 1000) / 10}%` : DASH}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    gap: 14, padding: "24px 0" }}>
        {r.networks.map((n) => (
          <div key={n.source} className="panel" style={{ padding: "16px 18px" }}>
            <div className="eyebrow">{n.label}</div>
            <div className="num" style={{ fontSize: 24, marginTop: 7 }}>{n.connected ? count(n.followers) : DASH}</div>
            <div style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 6 }}>
              {n.connected
                ? `${count(n.impressions)} impressions · ${n.engagement_rate != null ? `${Math.round(n.engagement_rate * 1000) / 10}%` : DASH} engagement`
                : "Not connected"}
            </div>
          </div>
        ))}
      </div>

      {missing.length > 0 && (
        <div className="panel" style={{ padding: "14px 18px", marginBottom: 22,
                                        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--text-2)" }}>
            Not connected: {missing.map((s) => s.label).join(", ")}.
          </span>
          <button type="button" className="btn-ghost" onClick={() => go({ name: "integrations" })}>
            Connect sources
          </button>
        </div>
      )}

      <Section title="Top posts" note={`${r.posts.length} in this window`}>
        {r.posts.length === 0 ? (
          <Empty hint="Connect Meta Social or X and sync to see posts." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Post</th>
                <th>Network</th>
                <th className="num">Impr</th>
                <th className="num">Likes</th>
                <th className="num">Comments</th>
                <th className="num">Shares</th>
                <th className="num">Rate</th>
              </tr>
            </thead>
            <tbody>
              {r.posts.map((p) => (
                <tr key={`${p.source}:${p.post_id}`}>
                  <td>
                    {p.post_url
                      ? <a href={p.post_url} target="_blank" rel="noreferrer">{(p.content_text ?? p.post_id).slice(0, 80)}</a>
                      : (p.content_text ?? p.post_id).slice(0, 80)}
                  </td>
                  <td style={{ fontSize: 12, color: "var(--text-3)" }}>{p.source === "x_social" ? "X" : "Meta"}</td>
                  <td className="num">{count(p.impressions)}</td>
                  <td className="num">{count(p.likes)}</td>
                  <td className="num">{count(p.comments)}</td>
                  <td className="num">{count(p.shares)}</td>
                  <td className="num">{p.engagement_rate != null ? `${Math.round(p.engagement_rate * 1000) / 10}%` : DASH}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </>
  );
}

function FunnelCell({ label, now, prev }: { label: string; now: number; prev: number }) {
  const d = delta(now, prev, "up");
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="num" style={{ fontSize: 24, letterSpacing: "-0.03em", marginTop: 6 }}>{count(now)}</div>
      <div className="num" style={{ fontSize: 12, color: d ? undefined : "var(--text-faint)", marginTop: 4 }}>
        {d ? d.label : "no prior window"}
      </div>
    </div>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <div style={{ padding: "22px 0", borderTop: "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, margin: 0 }}>{title}</h2>
        {note && <span style={{ fontSize: 12, color: "var(--text-faint)" }}>{note}</span>}
      </div>
      {children}
    </div>
  );
}

function Empty({ hint }: { hint: string }) {
  return <div style={{ fontSize: 13, color: "var(--text-3)", lineHeight: 1.5 }}>{hint}</div>;
}

function PageLoading() {
  return <div className="page" style={{ paddingTop: 48 }}><span className="sub">Loading demand…</span></div>;
}
function PageProblem({ message }: { message: string }) {
  return <div className="page" style={{ paddingTop: 48 }}><div className="form-error">{message}</div></div>;
}
